import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    DndContext,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
    type DragEndEvent,
    type DragStartEvent,
    DragOverlay,
} from '@dnd-kit/core';
import * as Popover from '@radix-ui/react-popover';
import { format } from 'date-fns';
import { AlertCircle, Loader2 } from 'lucide-react';
import type { CustomField, SavedView, Task } from '@/lib/schemas';
import { useI18n } from '@/lib/i18n';
import { useToast } from '@/hooks/use-toast';
import { useUpdateSavedView } from '@/hooks/use-views';
import { useUpdateTaskDate } from '@/hooks/use-calendar';
import { getSprintBands } from '@/hooks/use-calendar';
import {
    normaliseCalendarConfig,
    extractIsoDate,
    type NormalisedCalendarConfig,
} from './calendar-config';
import { applyCalendarFilters } from './calendar-filters';
import { CalendarToolbar } from './calendar-toolbar';
import { CalendarSettingsPanel } from './calendar-settings-panel';
import { CalendarMonth } from './calendar-month';
import { CalendarWeek } from './calendar-week';
import { CalendarAgenda } from './calendar-agenda';
import { CalendarEventCard } from './calendar-event-card';
import { colorToHex, resolveCalendarColor } from './calendar-config';

// ============================================
// CALENDAR VIEW
// ============================================
//
// t16: top-level Calendar component. Owns the anchor date, the
// (locally-overridden) view config, the settings popover state and
// the drag-and-drop wiring. Mirrors the saved-view-id split from t10
// so the *config* we render is derived from the live `activeView`
// prop — we only persist when the user explicitly opens the Save
// dialog from the Filters bar (the parent owns that flow).
//
// When the user drags a task pill from one day to another we PATCH
// /api/tasks/:id with the new date (see useUpdateTaskDate). The
// mutation is optimistic via useUpdateTaskDate's onMutate handler with
// rollback on error.

export interface CalendarViewProps {
    projectId: string;
    tasks: Task[];
    isLoading: boolean;
    error?: Error | null;
    customFields: CustomField[];
    activeView?: SavedView | null;
    onTaskClick?: (task: Task) => void;
    /** t18: parent opens the create-task dialog pre-filled with this date. */
    onCreateTaskAtDate?: (dateIso: string) => void;
    /** t18: parent opens the delete confirmation for this task id. */
    onDeleteTask?: (taskId: string) => void;
    canEdit?: boolean;
}

export function CalendarView({
    projectId,
    tasks,
    isLoading,
    error,
    customFields,
    activeView,
    onTaskClick,
    onCreateTaskAtDate,
    onDeleteTask,
    canEdit,
}: CalendarViewProps) {
    const { t } = useI18n();
    const { toast } = useToast();

    // Resolve the saved view's config + a small bit of local override
    // (anchor, settings popover open state, drag overlay). The
    // "currentConfig" is derived so we never copy server data into a
    // frozen useState copy.
    const savedConfig = useMemo(
        () => normaliseCalendarConfig(activeView?.config),
        [activeView?.config]
    );
    const [anchor, setAnchor] = useState<Date>(() => new Date());
    const [localOverride, setLocalOverride] = useState<NormalisedCalendarConfig | null>(null);
    const activeViewId = activeView?.id ?? null;
    // R2: when activeView is null there's no server row to PATCH — the
    // toolbar still has to work, so we keep a local override on top of
    // the defaults and reset it whenever the user picks a different
    // view (so stale state never bleeds across views).
    useEffect(() => {
        setLocalOverride(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeViewId]);
    const effectiveConfig = localOverride ?? savedConfig;
    // T21 R4: the popover state is now managed by Radix internally
    // (CalendarView renders <Popover.Root> wrapping the toolbar + panel,
    // and the toolbar's Settings button becomes <Popover.Trigger asChild>).
    // We dropped the manual `settingsOpen` state and the
    // `settingsAnchorRef` — both were the source of the "cog does
    // nothing" complaint because passing an HTMLElement to the Popover
    // Content as the `anchor` prop doesn't work (Radix Popper reads
    // the reference from the PopperContext, set by Popover.Anchor /
    // Popover.Trigger, not from a Content prop).
    const [activeDragTaskId, setActiveDragTaskId] = useState<string | null>(null);

    // Mutations: update the saved view's config (so a user can keep
    // their Month / Week / Agenda + dateSource + colorBy across
    // reloads) + update a task's date when dragged.
    const updateSavedView = useUpdateSavedView(projectId);
    const updateTaskDate = useUpdateTaskDate(projectId);

    // Sprint payload — only present when the project has a SPRINT
    // custom field.
    const sprintPayload = useMemo(() => getSprintBands(customFields), [customFields]);
    const sprintField = sprintPayload.field;
    const sprints = sprintPayload.sprints;

    // Filtered task list. The parent already passes filtered tasks
    // (via useTasks → saved view's filterConfig applied server-side),
    // but if the calendar-specific config diverges we re-apply here.
    const filteredTasks = useMemo(
        () => applyCalendarFilters(tasks, effectiveConfig.filters, customFields),
        [tasks, effectiveConfig.filters, customFields]
    );

    // Persist a config change by PATCHing the active view. Mirrors the
    // Kanban "Columns + Card fields" pattern: every change writes
    // immediately so the user doesn't lose work on reload.
    const handleConfigChange = useCallback(
        (next: NormalisedCalendarConfig) => {
            // Always reflect the change locally so the toolbar updates
            // instantly — even when we also persist.
            setLocalOverride(next);
            if (!activeView || !canEdit) return;
            const merged = {
                ...(activeView.config || {}),
                viewMode: next.viewMode,
                dateSource: next.dateSource,
                colorBy: next.colorBy,
                filters: next.filters,
                // T21 R4: include periods in the merge so the user's
                // custom date ranges survive a PATCH round-trip.
                periods: next.periods,
            };
            updateSavedView.mutate({ viewId: activeView.id, data: { config: merged as any } });
        },
        [activeView, updateSavedView, canEdit]
    );

    // dnd-kit sensors. PointerSensor with a tiny activation distance so
    // click events still fire (mirrors kanban-view.tsx).
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor)
    );

    const handleDragStart = useCallback((event: DragStartEvent) => {
        const data = event.active.data.current as any;
        if (data?.type === 'calendar-event') {
            setActiveDragTaskId(String(data.taskId));
        }
    }, []);

    const handleDragCancel = useCallback(() => {
        setActiveDragTaskId(null);
    }, []);

    const handleDragEnd = useCallback(
        async (event: DragEndEvent) => {
            setActiveDragTaskId(null);
            if (!event.over) return;
            const dragData = event.active.data.current as any;
            const dropData = event.over.data.current as any;
            if (dragData?.type !== 'calendar-event' || dropData?.type !== 'calendar-day') return;
            const taskId = String(dragData.taskId);
            const task = filteredTasks.find((tk) => tk.id === taskId);
            if (!task) return;

            const newDateIso = dropData.date as string;
            // t18: slice the drop date directly to YYYY-MM-DD instead of
            // going through `new Date(iso)` (which can shift the day back
            // for non-UTC timezones — the day-1 bug). The drop target
            // already sends a UTC ISO; the slice ignores the time and TZ.
            const newDate = extractIsoDate(newDateIso);

            try {
                await updateTaskDate.mutateAsync({
                    taskId,
                    newDate,
                    dateSource: effectiveConfig.dateSource,
                    projectId,
                    sprintId: null,
                    sprintFieldId: sprintField?.id,
                });
            } catch (err) {
                toast({
                    title: t('common.error'),
                    description: err instanceof Error ? err.message : t('calendar.errorUpdateDate'),
                    variant: 'destructive',
                });
            }
        },
        [filteredTasks, effectiveConfig.dateSource, projectId, sprintField?.id, updateTaskDate, toast, t]
    );

    // ----- Render -----
    if (error) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-center">
                <AlertCircle className="h-10 w-10 text-destructive mb-3" />
                <p className="text-sm text-foreground-muted">{error.message || t('calendar.loadError')}</p>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-foreground-muted" />
            </div>
        );
    }

    const draggedTask = activeDragTaskId ? filteredTasks.find((tk) => tk.id === activeDragTaskId) ?? null : null;

    return (
        // T21 R4: wrap the toolbar + settings panel in a single
        // <Popover.Root> so the toolbar's Settings button can be the
        // Popover.Trigger (which auto-registers as the anchor and
        // manages the open state). This replaces the previous "pass
        // anchorRef.current as the Content's anchor prop" hack that
        // silently failed because Popper.Content reads its reference
        // element from the PopperContext, NOT from a Content prop.
        <Popover.Root>
            <div className="space-y-3">
                <CalendarToolbar
                    anchor={anchor}
                    viewMode={effectiveConfig.viewMode}
                    onAnchorChange={setAnchor}
                    onViewModeChange={(m) => handleConfigChange({ ...effectiveConfig, viewMode: m })}
                />

                <DndContext
                    sensors={sensors}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragCancel={handleDragCancel}
                >
                    {effectiveConfig.viewMode === 'month' && (
                        <CalendarMonth
                            anchor={anchor}
                            tasks={filteredTasks}
                            customFields={customFields}
                            sprints={sprints}
                            sprintField={sprintField}
                            periods={effectiveConfig.periods}
                            colorBy={effectiveConfig.colorBy}
                            dateSource={effectiveConfig.dateSource}
                            onTaskClick={onTaskClick}
                            onCreateTaskAtDate={onCreateTaskAtDate}
                            onDeleteTask={onDeleteTask}
                            canEdit={canEdit}
                        />
                    )}
                    {effectiveConfig.viewMode === 'week' && (
                        <CalendarWeek
                            anchor={anchor}
                            tasks={filteredTasks}
                            customFields={customFields}
                            sprints={sprints}
                            sprintField={sprintField}
                            periods={effectiveConfig.periods}
                            colorBy={effectiveConfig.colorBy}
                            dateSource={effectiveConfig.dateSource}
                            onTaskClick={onTaskClick}
                            onCreateTaskAtDate={onCreateTaskAtDate}
                            onDeleteTask={onDeleteTask}
                            canEdit={canEdit}
                        />
                    )}
                    {effectiveConfig.viewMode === 'agenda' && (
                        <CalendarAgenda
                            anchor={anchor}
                            tasks={filteredTasks}
                            customFields={customFields}
                            sprints={sprints}
                            sprintField={sprintField}
                            periods={effectiveConfig.periods}
                            colorBy={effectiveConfig.colorBy}
                            dateSource={effectiveConfig.dateSource}
                            onTaskClick={onTaskClick}
                            onDeleteTask={onDeleteTask}
                        />
                    )}

                    <DragOverlay dropAnimation={null}>
                        {draggedTask ? (
                            <CalendarEventCard
                                task={draggedTask}
                                colorHex={colorToHex(
                                    resolveCalendarColor(draggedTask, effectiveConfig.colorBy, sprints, sprintField),
                                    customFields
                                )}
                                isStart
                                isEnd
                                isSingleDay
                                overlay
                                onClick={onTaskClick}
                            />
                        ) : null}
                    </DragOverlay>
                </DndContext>

                <CalendarSettingsPanel
                    config={effectiveConfig}
                    customFields={customFields}
                    hasSprintField={sprintField !== null}
                    onConfigChange={handleConfigChange}
                />
            </div>
        </Popover.Root>
    );
}

export default CalendarView;
